import React from 'react';
import Modal from 'react-modal';
import { Field, Form, Formik } from 'formik';
import { Oval } from 'react-loader-spinner';
import * as Yup from 'yup';
import toast from 'react-hot-toast';

import { useAppSelector, useAppDispatch } from '../../../app/hooks';
import { toggleShowEditDriverModal, updateDriverV2, getDriversV2, resetUpdateDriver } from '../../DriversV2/driversV2Slice';
import { ReactComponent as Close } from '../../../assets/images/close.svg';

import './EditDriverModal.scss';

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: "#FFFFFF"
  },
};

const validationSchema = Yup.object().shape({
  address: Yup.string()
    .required('Address is required'),
  zip: Yup.string()
    .required('Zip is required'),
  dlExpiration: Yup.string(),
  dotPhysicalExpiration: Yup.string(),
  startTime: Yup.string()
    .matches(/^([01]\d|2[0-3]):[0-5]\d$/, 'Start time must be HH:MM (24-hour)')
    .required('Start time is required'),
});

export interface EditDriverFormModel {
  address: string;
  zip: string;
  dlExpiration: string;
  dotPhysicalExpiration: string;
  startTime: string;
};

export function EditDriverModal() {
  const dispatch = useAppDispatch();

  const isOpen = useAppSelector(state => state.driversV2.showEditDriverModal);
  const selectedDriver = useAppSelector(state => state.driversV2.selectedDriver);
  const isLoading = useAppSelector(state => state.driversV2.updateDriver.loading);
  const error = useAppSelector(state => state.driversV2.updateDriver.error);
  const updatedDriver = useAppSelector(state => !!state.driversV2.updateDriver.value);

  React.useEffect(() => {
    if (!isLoading && !!error) {
      toast.error('Something went wrong. Please try again.');
    }

    if (!isLoading && !error && updatedDriver) {
      toast.success('Successfully updated driver.');
      dispatch(resetUpdateDriver());
      dispatch(getDriversV2());
    }
  }, [isLoading]);

  const toggleModal = () => {
    dispatch(toggleShowEditDriverModal());
  }

  const onSubmit = (form: EditDriverFormModel) => {
    if (selectedDriver) {
      dispatch(updateDriverV2({
        id: selectedDriver.id,
        request: form
      }));
    }
  }

  if (!selectedDriver) return null;

  const initialValues: EditDriverFormModel = {
    address: selectedDriver.address || '',
    zip: selectedDriver.zip || '',
    dlExpiration: selectedDriver.dlExpiration || '',
    dotPhysicalExpiration: selectedDriver.dotPhysicalExpiration || '',
    startTime: selectedDriver.startTime || '06:00',
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={toggleModal}
      style={modalStyles}
    >
      <div className='edit-driver-modal-component'>
        <Close className='close-button' onClick={toggleModal}/>

        <div className='title'>Edit Driver</div>

        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
          enableReinitialize
        >
          {({ errors, touched }) => (
            <Form className='edit-driver-form'>
              <div className='field-container'>
                <div className='label'>Address:</div>
                <Field id='address' name='address' className='text-input' placeholder='Address'/>
                {errors.address && touched.address && <div className='form-error'>{errors.address}</div>}
              </div>

              <div className='field-container'>
                <div className='label'>Zip:</div>
                <Field id='zip' name='zip' className='text-input' placeholder='Zip'/>
                {errors.zip && touched.zip && <div className='form-error'>{errors.zip}</div>}
              </div>

              <div className='field-container'>
                <div className='label'>DL Expiration:</div>
                <Field id='dlExpiration' name='dlExpiration' type='date' className='text-input'/>
              </div>

              <div className='field-container'>
                <div className='label'>DOT Physical Expiration:</div>
                <Field id='dotPhysicalExpiration' name='dotPhysicalExpiration' type='date' className='text-input'/>
              </div>

              <div className='field-container'>
                <div className='label'>Start Time:</div>
                <Field id='startTime' name='startTime' type='time' className='text-input'/>
                {errors.startTime && touched.startTime && <div className='form-error'>{errors.startTime}</div>}
              </div>

              <button type='submit' className='update-driver-button' disabled={isLoading}>
                Update Driver
                {isLoading && <div style={{ marginLeft: 8}}><Oval color="#131313" height={20} width={20} secondaryColor="#FBD20A"/></div>}
              </button>

              {!!error && (
                <div className='error-text'>
                  Something went wrong. Please try again.
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
}
